import { createContext, forwardRef, useContext, useId } from 'react'
import { useControllableValue } from '@/ui/kit'
import { cn } from './internal/utils'
import { BORDER, FOCUS_RING, RADIUS } from './internal/presets'

const GroupContext = createContext(null)

const Radio = forwardRef(
  ({ checked, defaultChecked, onChange, disabled = false, value, className, style, children, ...rest }, ref) => {
    const group = useContext(GroupContext)
    const [innerChecked, setInnerChecked] = useControllableValue(checked, defaultChecked ?? false)

    const isGrouped = Boolean(group)
    const isChecked = isGrouped ? group.value === value : innerChecked
    const isDisabled = disabled || group?.disabled

    const handleChange = (event) => {
      if (isGrouped) {
        group.select(value)
        onChange?.(event)
        return
      }
      setInnerChecked(event.target.checked)
      onChange?.(event)
    }

    return (
      <label
        className={cn(
          'ui-radio-wrapper inline-flex cursor-pointer items-center gap-2 text-sm',
          isDisabled && 'cursor-not-allowed opacity-60',
          className
        )}
        style={style}
      >
        <input
          ref={ref}
          type="radio"
          name={group?.name}
          checked={isChecked}
          disabled={isDisabled}
          value={value}
          onChange={handleChange}
          className={cn(
            'ui-radio-input h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full border border-[var(--border-strong)] bg-[var(--surface-card)] checked:border-[5px] checked:border-[var(--primary-color)] disabled:cursor-not-allowed',
            FOCUS_RING
          )}
          {...rest}
        />
        {children != null ? <span>{children}</span> : null}
      </label>
    )
  }
)
Radio.displayName = 'Radio'

/** antd's segmented radio: same value semantics, button chrome. */
const RadioButton = forwardRef(({ value, disabled = false, className, children, ...rest }, ref) => {
  const group = useContext(GroupContext)
  const isChecked = group?.value === value
  const isDisabled = disabled || group?.disabled

  return (
    <label
      className={cn(
        'ui-radio-button-wrapper inline-flex min-h-[var(--control-height)] cursor-pointer items-center border px-4 text-sm first:rounded-l-[var(--radius-sm)] last:rounded-r-[var(--radius-sm)] -ml-px first:ml-0',
        BORDER,
        isChecked
          ? 'ui-radio-button-wrapper-checked z-10 border-[var(--primary-color)] bg-[var(--primary-color)] text-white'
          : 'bg-[var(--surface-card)] text-[var(--text-body)] hover:text-[var(--primary-color)]',
        isDisabled && 'cursor-not-allowed opacity-60',
        className
      )}
    >
      <input
        ref={ref}
        type="radio"
        name={group?.name}
        checked={Boolean(isChecked)}
        disabled={isDisabled}
        value={value}
        onChange={() => group?.select(value)}
        className="sr-only"
        {...rest}
      />
      {children}
    </label>
  )
})
RadioButton.displayName = 'Radio.Button'

const Group = ({
  value,
  defaultValue,
  onChange,
  options,
  optionType,
  buttonStyle,
  disabled = false,
  name,
  className,
  style,
  children,
  ...rest
}) => {
  const generatedName = useId()
  const [innerValue, setInnerValue] = useControllableValue(value, defaultValue)

  const select = (next) => {
    setInnerValue(next)
    // antd hands onChange an event-shaped object.
    onChange?.({ target: { value: next }, currentTarget: { value: next } })
  }

  const context = { value: innerValue, select, disabled, name: name || generatedName }
  const ItemComponent = optionType === 'button' ? RadioButton : Radio

  return (
    <GroupContext.Provider value={context}>
      <div
        role="radiogroup"
        className={cn(
          'ui-radio-group',
          optionType === 'button' || buttonStyle
            ? 'inline-flex items-center'
            : 'flex flex-wrap items-center gap-3',
          /* Segmented Radio.Button children must sit flush (no gap-3) */
          '[&:has(.ui-radio-button-wrapper)]:inline-flex [&:has(.ui-radio-button-wrapper)]:flex-nowrap [&:has(.ui-radio-button-wrapper)]:items-center [&:has(.ui-radio-button-wrapper)]:gap-0',
          buttonStyle === 'solid' && 'ui-radio-group-solid',
          className
        )}
        style={style}
        {...rest}
      >
        {options?.length
          ? options.map((option) => {
              const resolved = typeof option === 'object' ? option : { label: option, value: option }
              return (
                <ItemComponent
                  key={String(resolved.value)}
                  value={resolved.value}
                  disabled={resolved.disabled}
                >
                  {resolved.label}
                </ItemComponent>
              )
            })
          : children}
      </div>
    </GroupContext.Provider>
  )
}

Radio.Group = Group
Radio.Button = RadioButton

export default Radio
